import {
  ComponentType,
  Suspense,
  lazy,
  useEffect,
  type ComponentProps,
  type ReactElement,
} from "react";
import { foregroundLoading } from "@/lib/foregroundLoading";

export function RouteLoadingFallback() {
  useEffect(() => {
    const token = foregroundLoading.beginWork("lazy_route", "Loading page");
    return () => foregroundLoading.end(token);
  }, []);
  return <div data-testid="lazy-route-fallback" className="sr-only" aria-hidden="true" />;
}

export function lazyPage<T extends ComponentType<Record<string, unknown>>>(
  importer: () => Promise<{ default: T }>,
): (props: ComponentProps<T>) => ReactElement {
  const Page = lazy(importer);

  function Wrapped(props: ComponentProps<T>) {
    const LazyPage = Page as ComponentType<ComponentProps<T>>;
    return (
      <Suspense fallback={<RouteLoadingFallback />}>
        <LazyPage {...props} />
      </Suspense>
    );
  }

  return Wrapped;
}

export function lazyNamedPage<T extends ComponentType<Record<string, unknown>>>(
  importer: () => Promise<Record<string, T>>,
  exportName: string,
): (props: ComponentProps<T>) => ReactElement {
  return lazyPage(() =>
    importer().then((mod) => {
      const page = mod[exportName];
      if (!page) {
        throw new Error(`lazyNamedPage: export "${exportName}" not found`);
      }
      return { default: page };
    }),
  );
}
